import { useGlobalContext } from '~/context/GlobalContext';
import { useState, useEffect, useMemo, useRef } from 'react';
import { IDataInvite } from '~/types/common';
import { MessageIcon } from '../Icons/Icons';
import { formatDate } from '~/utils/formatDate';
import { openAvatarLightbox } from '~/components/AvatarLightbox/openAvatarLightbox';
import classNames from 'classnames';
import styles from './PrivateChatInvitationContainer.module.css';

function PrivateChatInvitationContainer() {
  const { context } = useGlobalContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [filter, setFilter] = useState('');
  const [bottomOffset, setBottomOffset] = useState(0);

  // Ref do głównego kontenera widgetu
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const invites = context.privateWaitingUsers || [];
  

  // --- LOGIKA ZAMYKANIA POZA OBSZAREM ---
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      // Jeśli widget jest rozwinięty i kliknięcie nastąpiło poza containerRef
      if (
        isExpanded &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false);
        setFilter('');
      }
    };

    // Nasłuchujemy na całym dokumencie
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isExpanded]);

  // --- OBSŁUGA ZAPROSZEŃ ---
  const accept = (inviteId: string) => {
    window.dispatchEvent(new CustomEvent('privateAccept', {
      bubbles: true,
      detail: { type: 'privateAccept', inviteId }
    }));
  };

  const cancel = (inviteId: string) => {
    window.dispatchEvent(new CustomEvent('privateCancel', {
      bubbles: true,
      detail: { type: 'privateCancel', inviteId }
    }));
  };

  // --- LOGIKA POZYCJONOWANIA (iOS & Android) ---
  useEffect(() => {
    if (!window.visualViewport) return;

    const handleResize = () => {
      const vv = window.visualViewport!;
      const heightToSubtract = isExpanded ? 240 : 60;
      const offsetFromTop = vv.offsetTop + vv.height - heightToSubtract - 80;
      setBottomOffset(offsetFromTop);
    };

    window.visualViewport.addEventListener('resize', handleResize);
    window.visualViewport.addEventListener('scroll', handleResize);
    handleResize();

    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('scroll', handleResize);
    };
  }, [isExpanded, invites.length]);

  const processedInvites = useMemo(() => {
    const grouped = new Map<string, { invite: IDataInvite; count: number }>();
    invites.forEach((inv) => {
      const userId = inv.fromUser?.id || inv.fromUser?.name || 'unknown';
      if (grouped.has(userId)) {
        grouped.get(userId)!.count += 1;
      } else {
        grouped.set(userId, { invite: inv, count: 1 });
      }
    });

    const result = Array.from(grouped.values());
    if (!filter) return result;
    return result.filter((item) =>
      item.invite.fromUser?.name?.toLowerCase().includes(filter.toLowerCase())
    );
  }, [invites, filter]);

  if (invites.length === 0) return null;

  return (
    <div
      ref={containerRef} // Przypisanie refa
      data-component="PrivateChatInvitationContainer"
      className={classNames(styles.privateChatInvitationContainer, {
        [styles.expanded]: isExpanded,
        [styles.minimized]: !isExpanded,
      })}
      style={{ top: `${bottomOffset}px` }}
    >
      <div
        className={styles.header}
        onClick={() => {
          setIsExpanded(!isExpanded);
          if (isExpanded) setFilter('');
        }}
      >
        <div className={styles.iconWrapper}>
          <div className={styles.badge}>{invites.length}</div>
          {/* <div className={styles.mainIcon}>✉️</div> */}
          <MessageIcon />
        </div>

        {isExpanded && (
          <div className={styles.topControls}>
            <input
              ref={searchInputRef}
              className={styles.searchInput}
              placeholder="Szukaj..."
              autoFocus
              onClick={(e) => e.stopPropagation()} // Zapobiega zamknięciu przy kliknięciu w input
              onChange={(e) => setFilter(e.target.value)}
              value={filter}
            />
            <button
              className={styles.closeBtn}
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(false);
              }}
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {isExpanded && (
        <div className={styles.scrollArea}>
          {processedInvites.map(({ invite, count }, idx) => {
            const fromAvatar = invite.fromUser?.avatar || null;
            const fromName = invite.fromUser?.name || '';
            return (
              <div key={invite.inviteId || idx} className={styles.notificationCard}>
                <div className={styles.cardTop}>
                  <div className={styles.avatarWrapper}>
                    <button
                      type="button"
                      className={styles.avatarInner}
                      title={fromName ? `Pokaż zdjęcia: ${fromName}` : ''}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (fromName) openAvatarLightbox({ userName: fromName, avatarUrl: fromAvatar });
                      }}
                    >
                      {fromAvatar ? (
                        <img className={styles.avatarInnerPhoto} src={fromAvatar} alt={fromName} />
                      ) : (
                        (fromName.charAt(0) || '?').toUpperCase()
                      )}
                    </button>
                    {count > 1 && <div className={styles.multiCount}>x{count}</div>}
                  </div>
                  <div className={styles.textInfo}>
                    <div className={styles.nameRow}>
                      <span className={styles.userName}>{invite.fromUser?.name}</span>
                      <span className={styles.timeTag}>{formatDate(invite.time)}</span>
                    </div>
                    <div className={styles.userLabel}>Zaproszenie do rozmowy</div>
                  </div>
                </div>
                <div className={styles.cardActions}>
                  <button
                    className={classNames(styles.btnAction, styles.btnAccept)}
                    onClick={(e) => { e.stopPropagation(); accept(invite.inviteId); }}
                  >
                    ZAAKCEPTUJ
                  </button>
                  <button
                    className={classNames(styles.btnAction, styles.btnReject)}
                    onClick={(e) => { e.stopPropagation(); cancel(invite.inviteId); }}
                  >
                    ODRZUĆ
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  );
}

export default PrivateChatInvitationContainer;